import { useEffect, useState, useCallback } from "react";
import { Plus, Save, Trash2, Loader2 } from "lucide-react";

import { AdminHeader } from "@/components/admin/AdminHeader";
import { adminApi } from "@/lib/adminApi";

const emptyForm = {
  id: "",
  name: "",
  stream_url: "",
  color: "#d4af37",
  logo: "",
  order: 99,
  enabled: true,
  metadata_source: "icy",
  stats_url: "",
};

export default function AdminRadiosPage() {
  const [radios, setRadios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const { items } = await adminApi.listRadios();
      setRadios(items || []);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onEdit = (r) => {
    setEditingId(r.id);
    setForm({ ...r });
  };
  const onCancel = () => {
    setEditingId(null);
    setForm(emptyForm);
  };
  const onSave = async (e) => {
    e.preventDefault();
    if (!form.id.trim() || !form.name.trim() || !form.stream_url.trim()) return;
    setBusy(true);
    try {
      await adminApi.upsertRadio({
        ...form,
        order: parseInt(form.order, 10) || 0,
      });
      onCancel();
      refresh();
    } catch (err) {
      alert(err.detail || err.message);
    } finally {
      setBusy(false);
    }
  };
  const onDelete = async (r) => {
    if (!window.confirm(`Supprimer la radio "${r.name}" ?`)) return;
    await adminApi.deleteRadio(r.id);
    refresh();
  };

  return (
    <div className="mw-app">
      <AdminHeader />
      <main className="admin-main">
        <div className="admin-pagehead">
          <div>
            <span className="mw-section-eyebrow">Sources audio</span>
            <h1 className="admin-h1">Radios</h1>
            <p className="admin-lead">
              Ajoutez, modifiez ou supprimez les radios visibles dans la centrale de validation.
              Les changements sont instantanés (pas besoin de redéployer).
            </p>
          </div>
        </div>

        {loading ? (
          <div className="admin-loading"><Loader2 size={18} className="mw-spin" /> Chargement…</div>
        ) : (
          <div className="admin-radios-grid">
            <div className="admin-radios-list">
              {radios.map((r) => (
                <article key={r.id} className="admin-radio-row" style={{ "--art-accent": r.color }}>
                  <div className="admin-radio-color" />
                  <div className="admin-radio-info">
                    <div className="admin-radio-name">{r.name}</div>
                    <div className="admin-radio-id">id: {r.id}</div>
                    <div className="admin-radio-stream">{r.stream_url}</div>
                  </div>
                  <div className="admin-radio-actions">
                    <button type="button" className="mw-btn mw-btn-ghost" onClick={() => onEdit(r)}>
                      Modifier
                    </button>
                    <button
                      type="button"
                      className="admin-history-del"
                      onClick={() => onDelete(r)}
                      title="Supprimer"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </article>
              ))}
            </div>

            <form className="admin-radio-form" onSubmit={onSave}>
              <h3 className="admin-radio-form-title">
                {editingId ? `Modifier "${editingId}"` : "Ajouter une radio"}
              </h3>
              <label className="mw-label">
                Identifiant (sans espaces)
                <input
                  className="mw-input"
                  value={form.id}
                  onChange={upd("id")}
                  required
                  disabled={!!editingId}
                  placeholder="ex: jazz_radio"
                />
              </label>
              <label className="mw-label">
                Nom affiché
                <input className="mw-input" value={form.name} onChange={upd("name")} required />
              </label>
              <label className="mw-label">
                URL du stream Icecast
                <input
                  className="mw-input"
                  value={form.stream_url}
                  onChange={upd("stream_url")}
                  required
                  placeholder="https://stream.exemple.com:8000/stream"
                />
              </label>
              <div className="mw-form-row">
                <label className="mw-label">
                  Couleur (hex)
                  <input className="mw-input" value={form.color} onChange={upd("color")} />
                </label>
                <label className="mw-label">
                  Ordre
                  <input
                    type="number"
                    className="mw-input"
                    value={form.order}
                    onChange={upd("order")}
                  />
                </label>
              </div>
              <label className="mw-label">
                Logo (URL)
                <input className="mw-input" value={form.logo} onChange={upd("logo")} />
              </label>
              <div className="mw-form-row">
                <label className="mw-label">
                  Source des métadonnées
                  <select
                    className="mw-input"
                    value={form.metadata_source}
                    onChange={upd("metadata_source")}
                    data-testid="radio-form-meta-source"
                  >
                    <option value="icy">ICY (défaut — lit le stream MP3/AAC)</option>
                    <option value="icecast">Icecast (endpoint /status-json.xsl)</option>
                    <option value="none">Aucune (radio musicale sans titres)</option>
                  </select>
                </label>
                {form.metadata_source === "icecast" && (
                  <label className="mw-label">
                    URL stats (Icecast)
                    <input
                      className="mw-input"
                      value={form.stats_url}
                      onChange={upd("stats_url")}
                      placeholder="https://stream.exemple.com:8000/status-json.xsl"
                    />
                  </label>
                )}
              </div>
              <div className="admin-radio-form-actions">
                <button type="submit" className="mw-btn mw-btn-primary" disabled={busy}>
                  {busy ? <Loader2 size={14} className="mw-spin" /> : editingId ? <Save size={14} /> : <Plus size={14} />}
                  {editingId ? "Enregistrer" : "Ajouter"}
                </button>
                {editingId && (
                  <button type="button" className="mw-btn mw-btn-ghost" onClick={onCancel}>
                    Annuler
                  </button>
                )}
              </div>
            </form>
          </div>
        )}
      </main>
    </div>
  );
}
